import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { updateSchema, UpdatePermissionValidationSchema } from "@/features/permissions/schemas/create.schema";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useQueryClient } from "@tanstack/react-query";
import TextArea from "@/common/components/TextArea";
import { ShowHeader } from "@/common/components/ShowHeader";
import useTmaisPermission from "../../hooks/useTmaisPermission";
import { zodResolver } from "@hookform/resolvers/zod";
import useUpdateTmaisPermission from "../../hooks/useUpdateTmaisPermission";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const Update = () => {
  const { id } = useParams();

  const goBack = useGoBack();
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const queryClient = useQueryClient();

  const {
    data: initialTmaisPermission,
    isLoading
  } = useTmaisPermission(id || '');

  const {
    mutate,
    isPending
  } = useUpdateTmaisPermission({
    id: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmaisPermission', id] });
      goBack({ fallback: `/tmais-permissions/${id}` })
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    }
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToUpdateTmaisPermission =
    isUserPermissionsPending || hasUserPermission('Admin::Tmais::Permissions::Update')

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<UpdatePermissionValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      display_name: initialTmaisPermission?.display_name,
      description: initialTmaisPermission?.description,
    }
  });

  const onSubmit: SubmitHandler<UpdatePermissionValidationSchema> = (data) => mutate(data);

  return (
    <form 
      onSubmit={handleSubmit(onSubmit)} 
      autoComplete="false"
      className="flex flex-col max-h-[calc(100vh-64px)]"
    >
      <ShowHeader
        title="Permissões - Tmais"
        subtitle="Editar permissão"
        goBack
        goBackProps={{
          fallback: `/tmais-permissions/${id}`
        }}
        buttons={(
          <div className="flex gap-4">
            <Link to={`/tmais-permissions/${id}`}>
              <Button variant='outline-error'>
                Cancelar
              </Button>
            </Link>

            <Button 
              data-testid='update_tmais_permission' 
              type="submit" 
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" 
              isLoading={isPending}
              disabled={!userHasPermissionToUpdateTmaisPermission} 
            >
              Confirmar
            </Button>
          </div>
        )}
      />

      <ErrorMessageWrapper statusCode={userHasPermissionToUpdateTmaisPermission ? 200 : 403}>
        {isLoading ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="flex flex-col gap-4 flex-1 overflow-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            <p className="text-lg font-bold">
              Editar permissão
            </p>

            <div className="flex gap-2">
              <TextField
                label="Nome"
                markAsRequired
                data-testid="display_name_input"
                errorMessage={errors?.display_name?.message}
                defaultValue={initialTmaisPermission?.display_name}
                className="w-1/2"
                {...register('display_name')}
              />
              <TextField
                label="Classe de operação"
                data-testid="operation_klass_input"
                defaultValue={initialTmaisPermission?.operation_klass}
                className="w-1/2"
                disabled
              />
            </div>

            <TextField
              label="Descrição"
              markAsRequired
              data-testid="description_input"
              errorMessage={errors?.description?.message}
              defaultValue={initialTmaisPermission?.description}
              {...register('description')}
            />

            <TextArea
              label="Propiedades"
              placeholder="{}"
              defaultValue={JSON.stringify(initialTmaisPermission?.properties, null, 4)}
              rows={8}
              disabled
            />
          </Box>
        )}

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </form>
  );
};

export default Update;
